/**
 * Which record a shot was solved from, and who decided that.
 *
 * Two things write this provenance and they mean different things.
 *
 * The tool writes four fields on every AQ shot from app v2.4 on. Rather than
 * blindly storing the first gyro record after the operator's depth mark, it now
 * searches that shot's stationary period for the best record in it — see
 * `aq-merge-selection.md`. So `SourceGyroIndex` is present on essentially every
 * shot and means "the record this was solved from", `OriginalGyroIndex` is what
 * the old merge would have chosen, and the two differ on most shots.
 *
 *   SourceGyroIndex    the record actually used
 *   OriginalGyroIndex  the first record at or after the depth mark
 *   ClusterSize        how many records agreed; 1 means nothing corroborated it
 *   Reconstructed      the bore rate was discarded and rebuilt - not a measurement
 *   Unrepaired         nothing plausible was found in the window, by any method
 *
 * The server writes exactly one field, `SourceEditedBy`, and only from
 * SurveysController.EditShotSource. It appears nowhere in the Android app.
 *
 * That is the whole point of this module. The approval table's "edited" pill used
 * to key on `SourceGyroIndex` back when nothing but the web app ever wrote it;
 * doing that now marks a well-optimised survey as though someone had tampered
 * with it. `SourceEditedBy` is the only field that means a person intervened.
 */

/**
 * True when an admin re-sourced this shot through the approval page.
 *
 * Tests key presence rather than truthiness: the server writes
 * `User.Identity?.Name ?? ""`, so an empty string is still an edit, and gyro
 * record 0 is a legitimate index that a truthiness test would miss.
 */
export function isUserEdited(shot) {
    return !!shot && shot.SourceEditedBy !== undefined && shot.SourceEditedBy !== null;
}

/**
 * The username to attribute an edit to, or null when the shot was not edited by
 * a person or the server had no name to record.
 */
export function editedBy(shot) {
    return isUserEdited(shot) && shot.SourceEditedBy ? shot.SourceEditedBy : null;
}
